// src/components/Admin/Dashboard-Enhanced.js - Updated with clean design
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
  styled,
  alpha,
  CssBaseline,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft,
  Dashboard,
  Business,
  People,
  Assessment,
  Settings as SettingsIcon,
  Help,
  Brightness4,
  Brightness7,
  AccountCircle,
  Logout,
  HowToReg,
  Support,
  Notifications,
  LocalHospital,
  Key as KeyIcon,
  Person,
  Close,
  Description,
  Sync as SyncIcon,
  Forum as Hl7Icon,
  EventNote as WorklistIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import DashboardOverview from './DashboardOverview';
import FacilityManagementEnhanced from './FacilityManagement';
import UserManagement from './UserManagement';
import PendingApprovals from './PendingApprovals';
import SupportTickets from './SupportTickets';
import ProfileSettings from './ProfileSettings';
import UserSettings from '../UserSettings';
import AdminSettings from './AdminSettings';
import AnalyticsDashboard from './AnalyticsDashboard';
import HelpSupport from './HelpSupport';
import HL7MessageAuditLog from './HL7MessageAuditLog';
import MWLWorklistStatus from './MWLWorklistStatus';
import { createCustomTheme } from '../../theme/themeConfig';
import RadiologistManagement from './RadiologistManagement';
import AuditTrailReports from './AuditTrailReports';
import SecurityAudit from './SecurityAudit';
import IntegrationKeys from './IntegrationKeys';
import FullLogo from '../logo/FullLogo';
import { getUnreadNotifications, markAllNotificationsAsRead } from '../../services/notifications';
import { syncOrthanc } from '../../services/pacs';
import { formatPatientName } from '../../services/utils';
import NotificationListener from '../Common/NotificationListener';
import Badge from '@mui/material/Badge';

// Tesla-inspired color palette
const teslaColors = {
  dark: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#121212',
    surface: '#1E1E1E',
    card: 'rgba(30, 30, 30, 0.8)',
    text: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(255, 255, 255, 0.12)',
  },
  light: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#F5F5F7',
    surface: '#FFFFFF',
    card: 'rgba(255, 255, 255, 0.8)',
    text: '#121212',
    textSecondary: 'rgba(18, 18, 18, 0.7)',
    border: 'rgba(0, 0, 0, 0.12)',
  }
};

const drawerWidth = 220;
const collapsedDrawerWidth = 56;

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedDrawerWidth,
    height: '100%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'drawerOpen',
})(({ theme, selected, isDark, drawerOpen }) => ({
  borderRadius: '6px',
  margin: drawerOpen ? '2px 8px' : '2px auto',
  marginBottom: '3px',
  padding: drawerOpen ? '5px 10px' : '5px 0',
  width: drawerOpen ? 'calc(100% - 16px)' : '38px',
  height: '34px',
  minHeight: '34px',
  justifyContent: drawerOpen ? 'initial' : 'center',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  '&:hover': {
    backgroundColor: isDark 
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.08),
  },
  ...(selected && {
    backgroundColor: isDark 
      ? alpha(theme.palette.primary.main, 0.22)
      : theme.palette.primary.main,
    color: isDark ? theme.palette.primary.main : 'white',
    '& .MuiListItemIcon-root': {
      color: isDark ? theme.palette.primary.main : 'white',
    },
    '&:hover': {
      backgroundColor: isDark 
        ? alpha(theme.palette.primary.main, 0.28)
        : theme.palette.primary.dark,
    },
  }),
}));

const LogoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [currentView, setCurrentView] = useState('overview');
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const notificationButtonRef = React.useRef(null);
  const profileButtonRef = React.useRef(null);
  const { mode, toggleTheme } = useThemeMode();

  const customTheme = React.useMemo(() => createTheme(createCustomTheme(mode)), [mode]);

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const fetchNotifications = async () => {
    try {
      const data = await getUnreadNotifications();
      setUnreadCount(data.count || 0);
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleNotificationReceived = async (data) => {
    await fetchNotifications();
  };

  const handleNotificationMenuOpen = async (event) => {
    setNotificationAnchorEl(event?.currentTarget || notificationButtonRef.current);
    await fetchNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      await fetchNotifications();
      handleNotificationMenuClose();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, component: 'overview' },
    { text: 'Analytics', icon: <Assessment />, component: 'analytics' },
    { text: 'Facilities', icon: <Business />, component: 'facilities' },
    { text: 'User Management', icon: <People />, component: 'users' },
    { text: 'Radiologists', icon: <LocalHospital />, component: 'radiologists' },
    { text: 'Pending Approvals', icon: <HowToReg />, component: 'approvals' },
    { text: 'Support Tickets', icon: <Support />, component: 'tickets' },
    { text: 'Audit Trail Reports', icon: <Description />, component: 'audit-trail' },
    ...(user?.role === 'admin' ? [{ text: 'Security Audit', icon: <Description />, component: 'security-audit' }] : []),
    { text: 'HL7 Messages', icon: <Hl7Icon />, component: 'hl7-messages' },
    { text: 'MWL Worklist Status', icon: <WorklistIcon />, component: 'mwl-status' },
    { text: 'API Key Overview', icon: <KeyIcon />, component: 'integration-keys' },
    { text: 'System Settings', icon: <SettingsIcon />, component: 'settings' },
    { text: 'Help & Support', icon: <Help />, component: 'help' },
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuClick = (component) => {
    setCurrentView(component);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await syncOrthanc();
      setSyncMessage(res.message || 'Sync complete');
      setTimeout(() => setSyncMessage(''), 4000);
    } catch (err) {
      setSyncMessage('Sync failed: ' + (err.message || 'Unknown error'));
      setTimeout(() => setSyncMessage(''), 5000);
    } finally {
      setSyncing(false);
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'overview':
        return <DashboardOverview />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'facilities':
        return <FacilityManagementEnhanced />;
      case 'users':
        return <UserManagement />;
      case 'approvals':
        return <PendingApprovals />;
      case 'radiologists':
        return <RadiologistManagement />;
      case 'tickets':
        return <SupportTickets />;
      case 'audit-trail':
        return <AuditTrailReports />;
      case 'security-audit':
        return <SecurityAudit />;
      case 'hl7-messages':
        return <HL7MessageAuditLog />;
      case 'mwl-status':
        return <MWLWorklistStatus />;
      case 'profile':
        return <ProfileSettings />;
      case 'userSettings':
        return <UserSettings />;
      case 'integration-keys':
        return <IntegrationKeys />;
      case 'settings':
        return <AdminSettings />;
      case 'help':
        return <HelpSupport />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      {user && (
        <NotificationListener
          userId={user._id}
          onNotificationReceived={handleNotificationReceived}
          onSyncComplete={(data) => window.dispatchEvent(new CustomEvent('ORTHANC_SYNC_COMPLETE', { detail: data }))}
        />
      )}
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent', position: 'relative', zIndex: 1 }}>
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: customTheme.zIndex.drawer + 1,
            background: customTheme.palette.background.paper,
            boxShadow: 'none',
            borderBottom: `1px solid ${customTheme.palette.divider}`,
            color: customTheme.palette.text.primary,
          }}
        >
          <Toolbar sx={{ minHeight: { xs: '44px !important', sm: '48px !important' }, height: { xs: '44px', sm: '48px' }, px: { xs: 1, sm: 2 } }}>
            <IconButton
              aria-label="toggle drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{ 
                mr: 2,
                color: customTheme.palette.text.primary,
              }}
            >
              {drawerOpen ? <ChevronLeft /> : <MenuIcon />}
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <FullLogo />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title={syncMessage || 'Sync studies from Orthanc'}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSync}
                  disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
                  sx={{
                    mr: 1.5,
                    borderRadius: '8px',
                    borderColor: '#43e97b',
                    color: '#43e97b',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    '&:hover': { borderColor: '#38f9d7', backgroundColor: 'rgba(67,233,123,0.08)' },
                    '&.Mui-disabled': { borderColor: 'rgba(67,233,123,0.3)', color: 'rgba(67,233,123,0.4)' },
                  }}
                >
                  {syncing ? 'Syncing...' : 'Sync'}
                </Button>
              </span>
            </Tooltip>

            <IconButton
              onClick={toggleTheme}
              sx={{
                mr: 1,
                color: customTheme.palette.text.primary,
              }}
            >
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>

            <IconButton 
              ref={notificationButtonRef} 
              onClick={handleNotificationMenuOpen} 
              sx={{ 
                mr: 1,
                color: customTheme.palette.text.primary,
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.1)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  },
                }}
              >
                <Notifications />
              </Badge>
            </IconButton>

            <Menu
              anchorEl={notificationAnchorEl}
              open={Boolean(notificationAnchorEl)}
              onClose={handleNotificationMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  width: { xs: 'calc(100vw - 32px)', sm: 400 },
                  maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
                  maxHeight: 520,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  borderRadius: '14px',
                  boxShadow: customTheme.palette.mode === 'dark'
                    ? '0 12px 36px rgba(0,0,0,0.6)'
                    : '0 12px 36px rgba(0,0,0,0.12)',
                  border: `1px solid ${customTheme.palette.divider}`,
                },
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${customTheme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Notifications
                  </Typography>
                  {unreadCount > 0 && (
                    <Button size="small" onClick={handleMarkAllAsRead} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                      Mark all read
                    </Button>
                  )}
                </Box>
              </Box>
              {notifications.length > 0 ? (
                notifications.map((notification, index) => (
                  <MenuItem
                    key={notification._id || index}
                    onClick={() => {
                      if (notification.studyUID) {
                        navigate(`/viewer/${notification.studyUID}?reportId=${notification.reportId}`);
                      } else {
                        navigate(`/dashboard?reportId=${notification.reportId}`);
                      }
                      handleNotificationMenuClose();
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      whiteSpace: 'normal',
                      alignItems: 'flex-start',
                      borderBottom: index < notifications.length - 1 ? `1px solid ${customTheme.palette.divider}` : 'none',
                      '&:hover': {
                        backgroundColor: customTheme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: notification.read ? 400 : 600,
                          mb: 0.5,
                          color: notification.type === 'report_flagged' ? '#EF4444' : 'inherit',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          lineHeight: 1.45,
                        }}
                      >
                        {notification.type === 'lab_report_ready'
                          ? '🧪 '
                          : notification.type === 'report_flagged'
                            ? '🚩 '
                            : (notification.priority === 'stat' ? '🚨 ' : '📋 ')}
                        {(notification.message || (notification.type === 'report_flagged' ? 'Report Concerns Flagged' : 'Report Ready')).replace(/\^/g, ' ')}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: 1.3,
                        }}
                      >
                        {formatPatientName(notification.patientName)} • {notification.type === 'lab_report_ready' ? 'Laboratory Department' : (notification.modality || 'Imaging')}
                      </Typography>
                      {notification.timestamp && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.72rem' }}>
                          {new Date(notification.timestamp).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No new notifications
                  </Typography>
                </Box>
              )}
            </Menu>

            <IconButton 
              ref={profileButtonRef} 
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                color: customTheme.palette.text.primary,
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                }}
              >
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={() => setAnchorEl(null)}>
                <Avatar sx={{ width: 24, height: 24, mr: 2, bgcolor: customTheme.palette.primary.main }}>
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {user?.email}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setCurrentView('profile');
                  setAnchorEl(null);
                }}
              >
                <Person fontSize="small" sx={{ mr: 2 }} />
                Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setCurrentView('userSettings');
                  setAnchorEl(null);
                }}
              >
                <SettingsIcon fontSize="small" sx={{ mr: 2 }} />
                Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate('/portal');
                  setAnchorEl(null);
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '16px', opacity: 0.7 }}>
                  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                  <line x1="4" y1="22" x2="4" y2="15" />
                </svg>
                Switch Portal
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout fontSize="small" sx={{ mr: 2 }} />
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <StyledDrawer variant={isMobile ? 'temporary' : 'permanent'} open={drawerOpen} onClose={handleDrawerToggle}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              p: drawerOpen ? 1.5 : 0.75,
              pt: { xs: 8, sm: 8 },
              overflowX: 'hidden',
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            {/* Close button for mobile */}
            {isMobile && (
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  zIndex: 10,
                  opacity: 0.5,
                  '&:hover': {
                    opacity: 0.75,
                  },
                  display: { md: 'none' },
                }}
              >
                <Close />
              </IconButton>
            )}

            {/* Navigation - flex-1 for middle section */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                mt: 1,
                minHeight: 0,
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
                  borderRadius: '4px',
                },
              }}
            >
              <List sx={{ py: 0, px: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {menuItems.map((item) => {
                  const isActive = currentView === item.component;
                  const inactiveColor = mode === 'dark' ? '#CBD5E1' : '#6B7280';
                  const iconColor = isActive 
                    ? (mode === 'dark' ? customTheme.palette.primary.main : 'white')
                    : inactiveColor;
                  
                  return (
                    <Tooltip key={item.text} title={!drawerOpen ? item.text : ''} placement="right" arrow>
                      <StyledListItemButton
                        selected={isActive}
                        onClick={() => handleMenuClick(item.component)}
                        isDark={mode === 'dark'}
                        drawerOpen={drawerOpen}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: drawerOpen ? 28 : 0,
                            mr: drawerOpen ? 1 : 0,
                            color: iconColor,
                            justifyContent: 'center',
                            alignItems: 'center',
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.05rem',
                            },
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {drawerOpen && (
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontWeight: isActive ? 700 : 500,
                              fontSize: '0.78rem',
                              fontFamily: '"Satoshi", sans-serif',
                              color: isActive 
                                ? (mode === 'dark' ? customTheme.palette.primary.main : 'white')
                                : inactiveColor,
                            }}
                          />
                        )}
                      </StyledListItemButton>
                    </Tooltip>
                  );
                })}
              </List>
            </Box>

            {/* Divider and Logout section at bottom */}
            <Box sx={{ mt: 'auto', pt: 0.5, pb: 0.5, flexShrink: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Divider sx={{ my: 0.75, width: '100%', borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)' }} />
              <Tooltip title={!drawerOpen ? 'Logout' : ''} placement="right" arrow>
                <Button
                  onClick={handleLogout}
                  sx={{
                    width: drawerOpen ? 'calc(100% - 16px)' : 36,
                    minWidth: drawerOpen ? 'auto' : 36,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: drawerOpen ? 'flex-start' : 'center',
                    px: drawerOpen ? 1.5 : 0,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    fontFamily: '"Satoshi", sans-serif',
                    fontWeight: 700,
                    color: '#EF4444',
                    borderRadius: '6px',
                    gap: drawerOpen ? 1 : 0,
                    '&:hover': {
                      backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
                    },
                  }}
                >
                  <Logout sx={{ fontSize: '1.05rem', color: '#EF4444' }} />
                  {drawerOpen && (
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', color: '#EF4444' }}>
                      Logout
                    </Typography>
                  )}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </StyledDrawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2 },
            width: { sm: `calc(100% - ${drawerOpen ? drawerWidth : collapsedDrawerWidth}px)` },
            transition: customTheme.transitions.create(['width', 'margin'], {
              easing: customTheme.transitions.easing.sharp,
              duration: customTheme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Toolbar sx={{ minHeight: { xs: '44px !important', sm: '48px !important' }, height: { xs: '44px', sm: '48px' } }} />
          {renderContent()}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default AdminDashboard;
